// Synthetic large-transcript fixture for the M0.3 WKWebView memory profile.
//
// The B-vs-A decision gate (docs/design-ios-conversation-viewer.md §8 / §4)
// hinges on measuring peak WKWebView RSS while scrolling + playing a ~40-min
// conversation. Seeding a real 40-min bulk-import costs ElevenLabs $ and time;
// for a *front-end memory profile* a synthetic CLEANED transcript + a looped
// audio source is sufficient — the DOM/JS-heap pressure we're gating on comes
// from the number of turns and the waveform, not from real transcription.
//
// This module generates, entirely in-browser:
//   - a single CLEANED detail_items array shaped exactly like the backend's
//     `GET /conversations/{id}/detail_items?type=CLEANED_TRANSCRIPT` response
//     (turns in `data.text`, paired `data.turns_meta[]{role,start_ms,end_ms}`).
//   - a matching audio manifest (`total_duration_ms`, `audio_start_epoch_ms`).
//   - a matching peaks payload (`{peaks:[[min,max]...], max_value}`).
//
// Activated by the route's `?profile=1` query param (see
// EmbedConversationViewer). The audio element points at a looped sample WAV
// (also `?profile=1` controlled) so the waveform + playback head exercise the
// same code path without a real assembled-WAV endpoint.

// Default span: ~2400s = 40 minutes, ~1800 turns (≈ a turn every 1.3s).
export const PROFILE_DEFAULT_TURN_COUNT = 1800;
export const PROFILE_DEFAULT_TOTAL_MS = 2_400_000; // 40 min
// A fixed, non-zero epoch anchor — exercises the epoch→conv-relative
// normalization path (start_ms is stored epoch-flavored on real data).
export const PROFILE_AUDIO_START_EPOCH_MS = 1_700_000_000_000;

const ROLES = ["Customer", "Employee"];

// A small pool of varied utterance lengths so rows have realistic,
// non-uniform heights (the virtualizer / RSS profile should see mixed
// row sizes, not 1800 identical short lines).
const UTTERANCES = [
  "Yeah, that makes sense to me.",
  "Could you walk me through how the warranty works on this model, because I had a bad experience last time and want to make sure I understand exactly what's covered before I commit to anything?",
  "Sure.",
  "Let me check on that for you real quick.",
  "Honestly I'm not sure that's going to fit my budget, but I'm open to hearing the options if there's some flexibility on the financing side of things.",
  "We can definitely do that.",
  "Okay, so the next step would be to schedule a follow-up and get the paperwork started, and then I'll send you a summary over email so you have everything in writing.",
  "Perfect, thanks.",
  "I appreciate you taking the time to explain all of this in detail.",
  "No problem at all, that's what I'm here for.",
];

/**
 * Build a synthetic CLEANED-transcript fixture set.
 *
 * @param {object} opts
 * @param {number} opts.turnCount   number of turns to generate
 * @param {number} opts.totalMs     total audio duration in ms
 * @param {number} opts.epochMs     audio_start_epoch_ms anchor
 * @param {number} opts.bins        peaks bins
 * @returns {{ items, manifest, peaks }}
 */
export function buildProfileFixture({
  turnCount = PROFILE_DEFAULT_TURN_COUNT,
  totalMs = PROFILE_DEFAULT_TOTAL_MS,
  epochMs = PROFILE_AUDIO_START_EPOCH_MS,
  bins = 1500,
} = {}) {
  const perTurnMs = Math.floor(totalMs / turnCount);

  // Group turns into "detail items" the way the backend does — one CLEANED
  // item per ~5-min batch (≈ 8 items for 40 min). Each item carries its own
  // `text` (one labeled line per turn) + a positional `turns_meta`.
  const turnsPerBatch = Math.max(1, Math.ceil((turnCount / totalMs) * 300_000));
  const items = [];
  let batchIndex = 0;
  for (let start = 0; start < turnCount; start += turnsPerBatch) {
    const end = Math.min(start + turnsPerBatch, turnCount);
    const lines = [];
    const turnsMeta = [];
    for (let i = start; i < end; i += 1) {
      const role = ROLES[i % ROLES.length];
      const utterance = UTTERANCES[i % UTTERANCES.length];
      // CLEANED text shape: `Role (X): "utterance"` one per line, as the
      // scrubber emits and `parseTextTurns` expects.
      const tag = role === "Customer" ? "C" : "SP";
      lines.push(`${role} (${tag}): "${utterance}"`);
      const startMs = epochMs + i * perTurnMs;
      turnsMeta.push({
        role,
        start_ms: startMs,
        end_ms: startMs + Math.floor(perTurnMs * 0.8),
      });
    }
    items.push({
      id: 100 + batchIndex,
      index: batchIndex,
      type: "CLEANED_TRANSCRIPT",
      source_tagged_detail_item_id: 200 + batchIndex,
      data: {
        text: lines.join("\n"),
        turns_meta: turnsMeta,
        source_tagged_detail_item_id: 200 + batchIndex,
      },
    });
    batchIndex += 1;
  }

  const manifest = {
    conversation_id: "profile",
    total_duration_ms: totalMs,
    audio_start_epoch_ms: epochMs,
    batches: [],
  };

  // Synthetic peaks — a slowly-undulating envelope so the waveform renders
  // a recognizable shape (not flat) but stays a few KB. int16-scaled pairs.
  const peaksArr = new Array(bins);
  for (let i = 0; i < bins; i += 1) {
    const env = Math.abs(Math.sin((i / bins) * Math.PI * 12)) * 0.85 + 0.05;
    const amp = Math.round(env * 30000);
    peaksArr[i] = [-amp, amp];
  }
  const peaks = {
    conversation_id: "profile",
    duration_ms: totalMs,
    bins,
    sample_rate: 16000,
    max_value: 32767,
    peaks: peaksArr,
  };

  return { items, manifest, peaks };
}
